// Maps the real Apps Script payloads (French sheet columns) to the UI shape.
// Base is currently empty, so several MOC field names are best-effort guesses
// with fallbacks — adjust the left-hand keys here when real MOC records exist.

const objToChart = (obj, prefix = '') =>
  Object.entries(obj || {})
    .filter(([k, v]) => k && k.toLowerCase() !== 'non renseigné' && Number(v) > 0)
    .map(([k, v]) => ({ name: prefix ? `${prefix}${k}` : k, value: Number(v) || 0 }));

export const numCat = (v) => {
  if (v == null) return 3;
  const m = String(v).match(/[123]/);
  return m ? Number(m[0]) : 3;
};

// Real Statut labels (from the sheet's data-validation list) → internal key used for
// timeline/action logic. The exact French label is preserved as `statusLabel` for display.
const STATUS_ALIASES = [
  ['brouillon', 'brouillon'],
  ['prêt à cl', 'pret_cloture'], ['pret à cl', 'pret_cloture'], ['pret a cl', 'pret_cloture'], ['prêt a cl', 'pret_cloture'],
  ['archiv', 'cloturee'], ['clôtur', 'cloturee'], ['clotur', 'cloturee'],
  ['annul', 'annulee'], ['rejet', 'annulee'],
  ['prêt à impl', 'approbation'], ['pret à impl', 'approbation'], ['pret a impl', 'approbation'], ['prêt a impl', 'approbation'],
  ['pssr', 'implementation'], ['mise en route', 'implementation'], ['après mise', 'implementation'], ['apres mise', 'implementation'],
  ['implément', 'implementation'], ['implement', 'implementation'],
  ['approuv', 'approbation'], ['approb', 'approbation'],
  ['catégorie confirm', 'analyse_risques'], ['categorie confirm', 'analyse_risques'], ['analyse', 'analyse_risques'], ['risque', 'analyse_risques'],
  ['soumis', 'soumise'], ['évalu', 'soumise'], ['evalu', 'soumise'], ['révision', 'soumise'], ['revision', 'soumise'],
];
export const mapStatus = (s) => {
  if (!s) return 'brouillon';
  const low = String(s).toLowerCase();
  for (const [needle, key] of STATUS_ALIASES) if (low.includes(needle)) return key;
  return 'soumise';
};

const ACTION_STATUS = (s) => {
  const low = String(s || '').toLowerCase();
  if (low.includes('retard')) return 'en_retard';
  if (low.includes('term') || low.includes('réalis') || low.includes('realis') || low.includes('cloture') || low.includes('fait')) return 'terminee';
  return 'ouverte';
};

export const normDashboard = (d = {}) => ({
  kpis: {
    total: d.totalMocs ?? 0, open: d.mocsOuvertes ?? 0,
    cat1: d.mocsCategorie1 ?? 0, cat2: d.mocsCategorie2 ?? 0,
    temporary: d.changementsTemporaires ?? 0, urgent: d.changementsUrgents ?? 0,
    pssrPending: d.pssrEnAttente ?? 0, actionsOpen: d.actionsOuvertes ?? 0,
    actionsLate: d.actionsEnRetard ?? 0,
    avgDelayDays: d.delaiMoyenClotureJours ?? d.delaiMoyenApprobationJours ?? d.delaiMoyenEvaluationJours ?? 0,
    oldMocs: d.mocsNonCloturesDepuisXJours ?? 0,
  },
  byStatus: objToChart(d.parStatut),
  byCategory: objToChart(d.parCategorie, 'Cat. '),
  trend: [], // not provided by the API yet
});

export const normMoc = (m = {}) => {
  const urgent = m.Urgent === true || String(m.Urgent).toLowerCase() === 'true'
    || String(m.Nature_Changement || '').toLowerCase() === 'urgent';
  const types = m.Types_Modification_Multiples || m.Type_Modification || m.Types_Modification || m.modificationTypes;
  const rawCat = m.Categorie ?? m.Categorie_Finale ?? m.Categorie_Proposee ?? m.category;
  const hasCat = !(rawCat === '' || rawCat == null);
  return {
    id: m.MOC_ID || m.Moc_ID || m.id || '',
    title: m.Titre || m.Objet || m.title || '(sans titre)',
    site: m.Site_ID || m.Site || m.site || '',
    requester: m.Initiateur_ID || m.Pilote_MOC_ID || m.Demandeur_ID || m.CreatedBy || m.requester || '',
    category: hasCat ? numCat(rawCat) : null,
    nature: m.Nature_Changement || m.Nature || m.nature || '—',
    status: mapStatus(m.Statut || m.status),
    statusLabel: m.Statut || m.status || '',
    alert: urgent ? 'critical' : (m.Niveau_Alerte || 'none'),
    createdAt: m.CreatedAt || m.Date_Creation || m.createdAt || null,
    updatedAt: m.UpdatedAt || m.Date_MAJ || m.updatedAt || m.CreatedAt || null,
    dueAt: m.Date_Souhaitee_Implementation || m.Duree_Temporaire_Fin || m.Echeance || m.dueAt || null,
    department: m.Service || m.Departement || m.department || '',
    pssrRequired: !!(m.PSSR_Requise || m.PSSR_Requis || urgent || m.pssrRequired),
    currentSituation: m.Situation_Actuelle || m.currentSituation || '',
    futureSituation: m.Situation_Souhaitee || m.Situation_Future || m.futureSituation || '',
    modificationTypes: Array.isArray(types)
      ? types
      : (types ? String(types).split(',').map((s) => s.trim()).filter(Boolean) : []),
    openActions: m.Actions_Ouvertes ?? 0, lateActions: m.Actions_En_Retard ?? 0, ageDays: m.Age_Jours ?? 0,
    step: m.Etape_Courante || '',
    archived: m.Archive === true || String(m.Archive).toLowerCase() === 'true' || /supprim/i.test(String(m.Statut || '')),
    _raw: m,
  };
};

export const normAction = (a = {}) => {
  const due = a.Echeance || a.due || null;
  let status = ACTION_STATUS(a.Statut || a.status);
  if (status === 'ouverte' && due) {
    const d = new Date(due);
    if (!isNaN(d.getTime()) && d < new Date()) status = 'en_retard';
  }
  return {
    id: a.Action_ID || a.id || '',
    mocId: a.MOC_ID || a.mocId || '',
    title: a.Description || a.Titre || a.title || '',
    owner: a.Responsable_ID || a.owner || '',
    due,
    status,
    blocking: !!(a.Obligatoire_Avant_Demarrage || a.blocking),
    source: a.Origine || a.source || '',
  };
};
export const isRealAction = (a) => a.id || (a.title && a.title.trim());

const NOTIF_TYPE = (t) => {
  const s = String(t || '').toUpperCase();
  if (s.includes('APPROB') || s.includes('EVALU') || s.includes('VALID')) return 'validation';
  if (s.includes('ACTION')) return 'action';
  if (s.includes('PSSR')) return 'pssr';
  return 'info';
};
export const normNotification = (n = {}) => {
  const t = n.Type_Notification || n.Type || n.type || '';
  return {
    id: n.Notification_ID || n.id || '',
    type: NOTIF_TYPE(t),
    title: t ? String(t).replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase()) : 'Notification',
    body: n.Message || n.Corps || n.body || '',
    mocId: n.MOC_ID || n.mocId || null,
    read: !!(n.Lu || n.read) || String(n.Statut_Envoi || '').toLowerCase() === 'lue',
    at: n.Date_Envoi || n.CreatedAt || n.Date || n.at || null,
  };
};

// Detail: map the record and default nested collections (fetched separately / TBD).
export const normMocDetail = (m = {}) => ({
  ...normMoc(m),
  actions: Array.isArray(m.actions) ? m.actions.map(normAction).filter(isRealAction) : [],
  documents: Array.isArray(m.documents) ? m.documents : [],
  pssr: m.pssr || null,
});

export const normDocument = (d = {}) => {
  const name = d.Nom_Fichier || d.Nom || d.name || 'document';
  const mime = String(d.Mime || d.Type_Mime || d.Type || '').toLowerCase();
  const ext = String(name).split('.').pop().toLowerCase();
  const isPdf = mime.includes('pdf') || ext === 'pdf';
  return {
    id: d.Document_ID || d.id || '',
    name,
    category: d.Categorie || d.category || 'Autre',
    type: isPdf ? 'pdf' : 'image',
    size: d.Taille || d.size || '',
    version: d.Version || d.version || 1,
    archived: !!(d.Archive || d.archived),
    at: d.CreatedAt || d.at || null,
  };
};
